import { faCircleCheck, faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CircularProgress, Dialog, DialogContent, Grid, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { UserDTO } from "../../models/user";
import AddEditButton from "../../reusable-components/AddEditButton/AddEditButton";
import { UsersApiService } from "../../services/users/users-api.service";
import { EMAIL_PATTERN, isValidString } from "../../utils/validation/isValidString";
import ConfirmationChangesDialog from "../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../DialogActions/DialogActions";
import DialogTitle from "../DialogTitle/DialogTitle";

interface Props {
  handleAllowUser: (user: UserDTO) => void;
}

interface State {
  emailInput: string;
  open: boolean;
  loading: boolean;
  CCDOpen: boolean;
  isEmailValid: boolean;
  error: string;
  user: UserDTO | null;
  userChecked: boolean;
}

const AddAllowedUser = ({ handleAllowUser }: Props) => {
  const theme = useTheme();

  const INITIAL_STATE: State = {
    emailInput: "",
    open: false,
    loading: false,
    CCDOpen: false,
    isEmailValid: false,
    error: "",
    user: null,
    userChecked: false,
  };

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  //#region check is email valid
  const checkIsEmailValid = useCallback(() => {
    setState((state) => ({
      ...state,
      isEmailValid: isValidString(EMAIL_PATTERN, state.emailInput),
    }));
  }, [state.emailInput]);
  //#endregion

  //#region confirm
  const handleConfirmAllowedUser = useCallback(() => {
    if (state.user) {
      handleAllowUser(state.user);
      setState(INITIAL_STATE);
    }
  }, [state.user]);
  //#endregion

  //#region discard
  const handleDiscard = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);
  //#endregion

  //#region user exists
  const checkIfUserExists = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new UsersApiService().loadItems({}, state.emailInput.toLowerCase(), [], undefined, true);
      if (response.error) {
        setState((state) => ({
          ...state,
          error: response.error,
          user: null,
        }));
      } else {
        setState((state) => ({
          ...state,
          user: response.items[0] as UserDTO,
        }));
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({
        ...state,
        loading: false,
        userChecked: true,
      }));
    }
  }, [state.emailInput]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!!state.emailInput && isValidString(EMAIL_PATTERN, state.emailInput)) {
      timer = setTimeout(() => {
        checkIfUserExists();
      }, 1000);
    }

    return () => clearTimeout(timer);
  }, [state.emailInput]);
  //#endregion

  return (
    <>
      <AddEditButton
        title="Add allowed user to generate QR code"
        onClick={() => setState((state) => ({ ...state, open: true }))}
        effect="add"
      />
      <Dialog
        open={state.open}
        fullWidth
      >
        <DialogTitle
          title={"Add allowed user to generate QR code"}
          handleClose={() => setState((state) => ({ ...state, CCDOpen: true }))}
        />
        <DialogContent>
          <Grid style={{ display: "flex", alignItems: "center", gap: "15px", paddingTop: "15px" }}>
            <TextField
              label={"Enter user email"}
              value={state.emailInput}
              onChange={({ currentTarget }) => {
                setState((state) => ({
                  ...state,
                  emailInput: currentTarget.value,
                  user: null,
                  userChecked: false,
                }));
                checkIsEmailValid();
              }}
              error={!!state.emailInput && !state.isEmailValid}
              InputProps={{
                sx: {
                  width: "300px",
                },
                endAdornment: (
                  <>
                    {!!state.emailInput && !state.isEmailValid ? (
                      <Tooltip title={"Email address not valid"}>
                        <FontAwesomeIcon
                          icon={faCircleExclamation}
                          color={theme.palette.error.main}
                        />
                      </Tooltip>
                    ) : (
                      <></>
                    )}
                    {state.loading && (
                      <Tooltip title={"Checking if user exists in database"}>
                        <CircularProgress color="primary" />
                      </Tooltip>
                    )}
                  </>
                ),
              }}
            />
            {!!state.user && (
              <FontAwesomeIcon
                icon={faCircleCheck}
                color={theme.palette.success.main}
              />
            )}
            {!state.user && state.userChecked && (
              <>
                <FontAwesomeIcon
                  icon={faCircleExclamation}
                  color={theme.palette.warning.main}
                />
                <Typography
                  variant="body2"
                  color={theme.palette.warning.main}
                >
                  User does not exist in database
                </Typography>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions
          handleCancel={() => setState((state) => ({ ...state, CCDOpen: true }))}
          handleSave={handleConfirmAllowedUser}
          customSaveName="Confirm"
          disabledSaveButton={!state.user}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={handleDiscard}
      />
    </>
  );
};

export default AddAllowedUser;
